import { useState, useMemo, useEffect } from 'react'
import useDealStore from '../store/dealStore'
import useOrganizationStore from '../store/organizationStore'
import '../styles/module8.css'

const DEFAULT_OUTREACH_SIGNER = 'the Acquisitions Team'

function isPlatformBranded(value = '') {
  return /acquireos/i.test(String(value))
}

const money = (v) => `$${Math.round(v || 0).toLocaleString()}`
const pct = (v) => `${((v || 0) * 100).toFixed(2)}%`

// ─── Message Generation ───────────────────────────────────────────────────────
function generateMessage(channel, recipientType, recipientName, dealState, tone, extraNotes, orgProfile) {
  const raw = dealState.rawDealData || {}
  const fin = dealState.extractedFinancials || {}
  const uw = dealState.underwritingMetrics || {}
  const score = dealState.scoreAndOffer || {}
  const lm = (dealState.lenderMatches || {}).topRecommendation || {}

  const propName = dealState.dealName || 'the subject property'
  const units = Number(raw.property?.units) || fin.revenue?.rentRoll?.totalUnits || 0
  const city = raw.location?.city || 'the target market'
  const state = raw.location?.state || ''
  const location = [city, state].filter(Boolean).join(', ')
  const price = Number(raw.price) || 0
  const noi = Number(uw.noi || fin.noi) || 0
  const dscr = Number(uw.dscr?.current) || 0
  const capex = Number(uw.capex?.totalCapEx) || 0
  const capRate = pct(uw.capRate?.entry)
  const loanAmt = Number(lm.loanAmount || uw.debtService?.loanAmount) || price * 0.70
  const ltv = pct(lm.ltv || uw.debtService?.ltv)
  const dealScore = score.numericalScore || 0
  const lenderName = lm.lenderName || 'your institution'
  const loanProduct = lm.loanProduct || 'the requested loan product'
  // Use org profile for sender details
  const org = orgProfile || {}
  const senderName = org.senderName && !isPlatformBranded(org.senderName) ? org.senderName : DEFAULT_OUTREACH_SIGNER
  const companyName = org.companyName && !isPlatformBranded(org.companyName) ? org.companyName : ''
  const orgPhone = org.phone || ''
  const orgEmail = org.contactEmail || ''
  const orgWebsite = org.website || ''
  const sigLines = [senderName, companyName, orgPhone, orgEmail, orgWebsite].filter(Boolean)
  const signatureBlock = org.emailSignature && !isPlatformBranded(org.emailSignature)
    ? org.emailSignature
    : sigLines.join('\n')

  const toneIntro = {
    formal: 'I hope this message finds you well.',
    professional: 'Thank you for your time.',
    direct: "I'll keep this brief.",
  }[tone] || 'Thank you for your time.'

  if (channel === 'email') {
    if (recipientType === 'lender') {
      return {
        subject: `Multifamily Acquisition – ${units ? units + '-Unit ' : ''}${location}`,
        body: `Dear ${recipientName || lenderName},\n\n${toneIntro} I am reaching out regarding a ${units || 'multifamily'}-unit acquisition opportunity located in ${location} that I believe aligns well with ${lenderName}'s lending criteria.\n\nDeal Summary:\n• Property: ${propName}\n• Location: ${location}\n• Units: ${units || 'TBD'}\n• Purchase Price: ${money(price)}\n• NOI: ${money(noi)}\n• Cap Rate: ${capRate}\n• DSCR: ${dscr ? dscr.toFixed(2) + 'x' : 'TBD'}\n• Loan Request: ${money(loanAmt)} (${ltv} LTV)\n• Deal Score: ${dealScore}/100${capex ? '\n• CapEx Budget: ' + money(capex) : ''}${loanProduct !== 'the requested loan product' ? '\n• Loan Product: ' + loanProduct : ''}\n\n${extraNotes ? 'Additional context: ' + extraNotes + '\n\n' : ''}I would love to schedule a brief call to walk through the package details. The full loan request document, rent roll, T12, and underwriting summary are available upon request.\n\nPlease let me know your availability or any preliminary questions.\n\nBest regards,\n${signatureBlock}`,
      }
    }
    if (recipientType === 'broker') {
      return {
        subject: `Acquisition Interest – ${propName}${location ? ' (' + location + ')' : ''}`,
        body: `Dear ${recipientName || 'Team'},\n\n${toneIntro} I wanted to reach out to formally express my interest in ${propName}${location ? ' located in ' + location : ''}.\n\nAfter reviewing the available information and running initial underwriting, the property scores ${dealScore}/100 on our acquisition model and fits our target profile.\n\nOur Offer Range: ${score.offerPrice ? money(score.offerPrice) : 'Competitive with market'}\n${price ? '• List Price: ' + money(price) + '\n' : ''}${units ? '• Units: ' + units + '\n' : ''}${noi ? '• Underwritten NOI: ' + money(noi) + '\n' : ''}${extraNotes ? '\nAdditional Notes: ' + extraNotes + '\n' : ''}\nWe are well-capitalized and ready to move quickly. Please share any additional documentation (rent roll, T12, inspection reports) so we can move toward a formal offer.\n\nLooking forward to your response.\n\nBest regards,\n${signatureBlock}`,
      }
    }
    if (recipientType === 'seller') {
      return {
        subject: `Offer of Interest – ${propName}`,
        body: `Dear ${recipientName || 'Seller'},\n\n${toneIntro} We are reaching out with a serious interest in acquiring ${propName}${location ? ' in ' + location : ''}.\n\nBased on our analysis, we are prepared to present a formal offer. Our team has completed a full underwriting review and we believe we can offer favorable terms and a smooth closing process.\n\n${price ? '• Asking Price: ' + money(price) + '\n' : ''}${units ? '• Units: ' + units + '\n' : ''}${score.offerPrice ? '• Our Target Offer: ' + money(score.offerPrice) + '\n' : ''}${extraNotes ? '\nNotes: ' + extraNotes + '\n' : ''}\nWe are flexible on closing timeline and structure. Please let us know the best way to proceed.\n\nWarm regards,\n${signatureBlock}`,
      }
    }
    return {
      subject: `Re: ${propName} – ${location}`,
      body: `Dear ${recipientName || 'Team'},\n\n${toneIntro} I am following up regarding ${propName}${location ? ' (' + location + ')' : ''}.\n\n${units ? 'The property consists of ' + units + ' units' : 'The subject property'} is currently under active review. Our underwriting indicates an NOI of ${money(noi)}, a cap rate of ${capRate}, and a deal score of ${dealScore}/100.\n\n${extraNotes ? extraNotes + '\n\n' : ''}Please feel free to reach out with any questions.\n\nBest,\n${signatureBlock}`,
    }
  }

  if (channel === 'sms') {
    const short = {
      lender: `Hi ${recipientName || 'there'}, reaching out re: ${units ? units + '-unit ' : ''}multifamily in ${city}. Purchase: ${money(price)}, NOI: ${money(noi)}, DSCR: ${dscr ? dscr.toFixed(2) + 'x' : 'TBD'}. Can we connect? — ${senderName}`,
      broker: `Hi ${recipientName || 'there'}, interested in ${propName} in ${city}. Offer range: ${score.offerPrice ? money(score.offerPrice) : 'competitive'}. Ready to move fast. Can we talk? — ${senderName}`,
      seller: `Hi ${recipientName || 'there'}, seriously interested in ${propName}. Can offer favorable terms & quick close. Would love to connect. — ${senderName}`,
      other: `Hi ${recipientName || 'there'}, following up on ${propName} in ${city}. ${extraNotes || 'Happy to share details. Can we connect?'} — ${senderName}`,
    }
    return { subject: null, body: short[recipientType] || short.other }
  }

  if (channel === 'callscript') {
    const opener = recipientType === 'lender'
      ? `Hi, may I speak with ${recipientName || 'the lending team'}? … Hi ${recipientName || 'there'}, this is [Your Name] from [Your Company]. Thanks for taking my call.`
      : `Hi, may I speak with ${recipientName || 'the right person'}? … Hi ${recipientName || 'there'}, my name is [Your Name] with [Your Company]. Thanks for your time.`

    const pitch = recipientType === 'lender'
      ? `I'm calling about a ${units || 'multifamily'}-unit acquisition in ${location}. The property is ${propName}, priced at ${money(price)}. We're looking for a loan of approximately ${money(loanAmt)} — roughly ${ltv} LTV. The NOI underwrites to ${money(noi)} and the DSCR is ${dscr ? dscr.toFixed(2) + 'x' : 'solid'}. Deal score on our platform is ${dealScore}/100. Does this fit within your current lending parameters?`
      : recipientType === 'broker'
        ? `I'm calling about ${propName} in ${location}. We've completed full underwriting and are very interested — deal scores ${dealScore}/100 on our model. Our offer range is ${score.offerPrice ? money(score.offerPrice) : 'competitive with market'}. Can we set up a time to discuss?`
        : `I'm calling to follow up on ${propName}. We've done our homework — NOI of ${money(noi)}, ${units} units, priced at ${money(price)}. We're ready to move forward quickly.`

    return {
      subject: null,
      body: `CALL SCRIPT — ${propName}\n─────────────────────────────\n\n[OPENER]\n${opener}\n\n[PITCH]\n${pitch}\n\n[KEY METRICS TO REFERENCE]\n• Property: ${propName}\n• Location: ${location}\n• Units: ${units || 'TBD'}\n• Purchase Price: ${money(price)}\n• NOI: ${money(noi)}\n• Cap Rate: ${capRate}\n• DSCR: ${dscr ? dscr.toFixed(2) + 'x' : 'TBD'}${capex ? '\n• CapEx: ' + money(capex) : ''}\n• Deal Score: ${dealScore}/100\n\n[IF ASKED ABOUT TIMELINE]\n"We're targeting a ${lm.closingTimeline || '30-45 day'} close. We have financing lined up and are fully prepared to move forward quickly."\n\n[CLOSE]\n"Can I send over the full package — rent roll, T12, underwriting summary — for your review?"\n\n[NOTES]\n${extraNotes || '(Add any custom talking points here before your call)'}\n─────────────────────────────`,
    }
  }

  return { subject: '', body: '' }
}

// ─── Follow-up sequence ───────────────────────────────────────────────────────
function buildFollowUps(recipientType, recipientName, propName, location) {
  const name = recipientName || (recipientType === 'lender' ? 'the lender' : recipientType === 'broker' ? 'the broker' : 'the seller')
  return [
    {
      day: 3,
      label: 'Day 3 Check-In',
      body: `Hi ${name}, just circling back on ${propName}${location ? ' in ' + location : ''}. Did you have a chance to review the package I sent? Happy to answer any questions or provide additional documentation. Looking forward to connecting.`,
    },
    {
      day: 7,
      label: 'Day 7 Follow-Up',
      body: `Hi ${name}, following up one more time on ${propName}. We remain very interested and are prepared to move quickly. If now isn't the right time, I'd appreciate knowing so I can adjust our timeline. Thanks for your consideration.`,
    },
    {
      day: 14,
      label: 'Day 14 Final Touch',
      body: `Hi ${name}, last follow-up on ${propName}. We've kept our offer position open but will need to finalize our plans by end of week. Please let me know if there's any opportunity to connect before then. Appreciate your time.`,
    },
  ]
}

const INITIAL_TEMPLATES = [
  { id: 't1', name: 'Lender Initial Inquiry', channel: 'email', recipientType: 'lender', preview: 'Multifamily Acquisition – [Units]-Unit [City]…' },
  { id: 't2', name: 'Broker Interest Email', channel: 'email', recipientType: 'broker', preview: 'Acquisition Interest – [Property Name]…' },
  { id: 't3', name: 'Quick Lender SMS', channel: 'sms', recipientType: 'lender', preview: 'Hi, reaching out re: multifamily in [City]…' },
]

const CHANNEL_LABELS = { email: 'Email', sms: 'SMS / Text', callscript: 'AI Call Assistant' }
const RECIPIENT_LABELS = { lender: 'Lender', broker: 'Broker', seller: 'Seller', other: 'Attorney / Other' }
const channelIcon = { email: '✉️', sms: '💬', callscript: '📞' }
const recipientIcon = { lender: '🏦', broker: '🤝', seller: '🏠', other: '👤' }

const RECIPIENT_CONTEXT = {
  lender: {
    target: 'Lender',
    purpose: 'Financing outreach for loan terms, DSCR review, rate, LTV, and package review.',
    subjectLabel: 'Lender subject lines',
    replyPlaceholder: 'Paste a lender reply here...',
  },
  broker: {
    target: 'Broker',
    purpose: 'Broker outreach for deal access, OM/rent roll/T12 requests, showing process, and offer timing.',
    subjectLabel: 'Broker subject lines',
    replyPlaceholder: 'Paste a broker reply here...',
  },
  seller: {
    target: 'Seller',
    purpose: 'Seller outreach for purchase interest, offer positioning, timing, and next-step conversation.',
    subjectLabel: 'Seller subject lines',
    replyPlaceholder: 'Paste a seller reply here...',
  },
  other: {
    target: 'Attorney / Other',
    purpose: 'Professional outreach for legal, diligence, escrow, title, or other transaction coordination.',
    subjectLabel: 'Attorney / other subject lines',
    replyPlaceholder: 'Paste an attorney or transaction-party reply here...',
  },
}

const THREAD_PARTIES = [
  { key: 'lender', label: 'Lender' },
  { key: 'broker', label: 'Broker' },
  { key: 'other', label: 'Attorney' },
]

function buildSubjectVariants(propName, location, units, dealScore) {
  const market = location || 'Target Market'
  return [
    { id: 'direct', label: 'Direct', subject: `Multifamily Acquisition - ${units ? units + '-Unit ' : ''}${market}`, lift: dealScore >= 80 ? 18 : 12 },
    { id: 'value', label: 'Value-led', subject: `${propName}: Financing-ready acquisition review`, lift: dealScore >= 75 ? 15 : 9 },
    { id: 'timing', label: 'Timing', subject: `Quick review request - ${propName}`, lift: 11 },
  ]
}

function buildResponseSuggestion(reply, recipientType, propName) {
  const text = reply.toLowerCase()
  if (!reply.trim()) {
    return `Thanks for the response. We can send the full package for ${propName}, including underwriting, rent roll, T12, and next-step timing. Please let us know what format is easiest for your review.`
  }
  if (text.includes('rate') || text.includes('terms') || text.includes('ltv')) {
    return `Thanks for the quick reply. We are happy to review preliminary terms and can share the underwriting package, target loan amount, DSCR support, and requested LTV for ${propName}. What additional assumptions would help you sharpen pricing?`
  }
  if (text.includes('document') || text.includes('rent roll') || text.includes('t12') || text.includes('package')) {
    return `Absolutely. We can send the rent roll, T12, underwriting summary, and acquisition memo for ${propName}. Please confirm the best email address and any required intake checklist.`
  }
  if (text.includes('not interested') || text.includes('pass') || text.includes('decline')) {
    return `Understood, and thank you for taking a look. We will keep you in mind for future opportunities that better match your criteria. If helpful, please share any quick feedback on why ${propName} is not a fit.`
  }
  return recipientType === 'broker'
    ? `Thanks for the update. We remain interested in ${propName} and can move quickly. Please send any available diligence files, seller timing expectations, and preferred LOI process.`
    : `Thanks for the response. We remain active on ${propName} and can provide any additional underwriting detail needed to move the conversation forward.`
}

function checkCompliance(subject, body) {
  const text = `${subject} ${body}`.toLowerCase()
  return [
    { label: 'Professional language', pass: !/(guaranteed|no risk|can't lose|must accept)/i.test(text) },
    { label: 'Non-binding language', pass: text.includes('non-binding') || text.includes('subject to') },
    { label: 'Clear next step', pass: /(call|connect|review|send|availability|questions)/i.test(text) },
    { label: 'No unsupported claims', pass: !/(approved financing|guaranteed close|certain return)/i.test(text) },
  ]
}

// ─── Component ────────────────────────────────────────────────────────────────
function Module8Page() {
  const { dealState, updateDealState, updateAvailableModules, forceNavigateToModule } = useDealStore()
  const { profile: orgProfile } = useOrganizationStore()

  const savedOutreach = dealState.outreachDraft || {}
  const [channel, setChannel] = useState(savedOutreach.channel || 'email')
  const [recipientType, setRecipientType] = useState(savedOutreach.recipientType || 'lender')
  const [recipientName, setRecipientName] = useState(savedOutreach.recipientName || '')
  const tone = 'professional'
  const [extraNotes, setExtraNotes] = useState(savedOutreach.extraNotes || '')
  const [generated, setGenerated] = useState(false)
  const [editableBody, setEditableBody] = useState(savedOutreach.editableBody || '')
  const [editableSubject, setEditableSubject] = useState(savedOutreach.editableSubject || '')
  const [copied, setCopied] = useState(false)
  const [savedTemplates, setSavedTemplates] = useState(
    savedOutreach.savedTemplates || INITIAL_TEMPLATES
  )
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [activeFollowUp, setActiveFollowUp] = useState(null)
  const [completedCount, setCompletedCount] = useState(savedOutreach.completedCount || 0)
  const [threadParties, setThreadParties] = useState(savedOutreach.threadParties || ['lender', 'broker'])
  const [incomingReply, setIncomingReply] = useState('')
  const [crmProvider, setCrmProvider] = useState(savedOutreach.crmProvider || 'Gmail')
  const [crmLog, setCrmLog] = useState(savedOutreach.crmLog || [])

  // Auto-persist outreach state
  useEffect(() => {
    updateDealState({ outreachDraft: {
      channel, recipientType, recipientName, extraNotes,
      editableBody, editableSubject,
      savedTemplates, completedCount, threadParties, crmProvider, crmLog,
    }})
  }, [channel, recipientType, recipientName, extraNotes, editableBody, editableSubject, // eslint-disable-line
      savedTemplates, completedCount, threadParties, crmProvider, crmLog]) // eslint-disable-line

  const raw = dealState.rawDealData || {}
  const fin = dealState.extractedFinancials || {}
  const uw = dealState.underwritingMetrics || {}

  // ── Editable deal context (local overrides, default to store values) ──────────
  const [dealCtx, setDealCtx] = useState({
    propName: dealState.dealName || (raw.location?.city ? `${raw.location.city} Deal` : 'Active Deal'),
    city: raw.location?.city || '',
    state: raw.location?.state || '',
    units: String(Number(raw.property?.units) || fin.revenue?.rentRoll?.totalUnits || 0),
    price: String(Number(raw.price) || 0),
    noi: String(Number(uw.noi || fin.noi) || 0),
    capRate: String(Number(uw.capRate?.entry) || 0),
  })
  const [showDealCtxEdit, setShowDealCtxEdit] = useState(false)

  const updateCtx = (field, value) => setDealCtx(c => ({ ...c, [field]: value }))

  const propName = dealCtx.propName
  const location = [dealCtx.city, dealCtx.state].filter(Boolean).join(', ')
  const units = Number(dealCtx.units) || 0
  const price = Number(dealCtx.price) || 0
  const dealScore = Number(dealState.scoreAndOffer?.numericalScore) || Number(raw.initialAIScore) || 0

  // Build a memoized synthetic dealState with user overrides applied (for generateMessage)
  const effectiveDealState = useMemo(() => ({
    ...dealState,
    dealName: dealCtx.propName,
    rawDealData: {
      ...raw,
      price: Number(dealCtx.price) || raw.price,
      location: {
        ...(raw.location || {}),
        city: dealCtx.city || raw.location?.city,
        state: dealCtx.state || raw.location?.state,
      },
      property: {
        ...(raw.property || {}),
        units: Number(dealCtx.units) || raw.property?.units,
      },
    },
    underwritingMetrics: {
      ...uw,
      noi: Number(dealCtx.noi) || uw.noi,
      capRate: { ...(uw.capRate || {}), entry: Number(dealCtx.capRate) || uw.capRate?.entry },
    },
    extractedFinancials: {
      ...fin,
      noi: Number(dealCtx.noi) || fin.noi,
    },
  }), [dealState, dealCtx, raw, fin, uw])

  const followUps = useMemo(
    () => buildFollowUps(recipientType, recipientName, propName, location),
    [recipientType, recipientName, propName, location]
  )

  const subjectVariants = useMemo(
    () => buildSubjectVariants(propName, location, units, dealScore),
    [propName, location, units, dealScore]
  )
  const recipientContext = RECIPIENT_CONTEXT[recipientType] || RECIPIENT_CONTEXT.other

  const threadMessages = useMemo(
    () => threadParties.map((party) => ({
      party,
      label: THREAD_PARTIES.find((item) => item.key === party)?.label || RECIPIENT_LABELS[party] || 'Stakeholder',
      message: generateMessage('email', party, '', effectiveDealState, tone, `Coordinate this outreach with the broader ${propName} stakeholder thread.`, orgProfile),
    })),
    [threadParties, effectiveDealState, tone, propName]
  )

  const responseSuggestion = useMemo(
    () => buildResponseSuggestion(incomingReply, recipientType, propName),
    [incomingReply, recipientType, propName]
  )

  const complianceChecks = useMemo(
    () => checkCompliance(editableSubject, editableBody),
    [editableSubject, editableBody]
  )
  const complianceScore = complianceChecks.filter((check) => check.pass).length

  const handleGenerate = () => {
    const msg = generateMessage(channel, recipientType, recipientName, effectiveDealState, tone, extraNotes, orgProfile)
    setEditableSubject(msg.subject || '')
    setEditableBody(msg.body || '')
    setGenerated(true)
    setCopied(false)
  }

  const handleCopy = () => {
    const text = channel === 'email' && editableSubject
      ? `Subject: ${editableSubject}\n\n${editableBody}`
      : editableBody
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2200)
    })
  }

  const handleOpenEmail = () => {
    if (channel !== 'email' || !generated) return
    const subject = encodeURIComponent(editableSubject || '')
    const body = encodeURIComponent(editableBody || '')
    window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=&su=${subject}&body=${body}`, '_blank', 'noopener,noreferrer')
    setCompletedCount((c) => c + 1)
  }

  const handleSaveTemplate = () => {
    if (!generated) return
    const id = `t_${Date.now()}`
    setSavedTemplates((prev) => [
      ...prev,
      {
        id,
        name: `${RECIPIENT_LABELS[recipientType]} ${CHANNEL_LABELS[channel]}`,
        channel,
        recipientType,
        preview: (editableSubject || editableBody).slice(0, 52) + '…',
        subject: editableSubject,
        body: editableBody,
      },
    ])
  }

  const handleDeleteTemplate = (id) => setSavedTemplates((prev) => prev.filter((t) => t.id !== id))
  const handleOpenTemplate = (template) => {
    const details = template.body
      ? { subject: template.subject || '', body: template.body }
      : generateMessage(template.channel, template.recipientType, '', effectiveDealState, tone, '', orgProfile)

    setSelectedTemplate({
      ...template,
      subject: details.subject || '',
      body: details.body || template.preview || '',
    })
  }

  const toggleThreadParty = (party) => {
    setThreadParties((current) => (
      current.includes(party)
        ? current.filter((item) => item !== party)
        : [...current, party]
    ))
  }

  const handleCopyThread = () => {
    const text = threadMessages.map(({ label, message }) => (
      `${label}\nSubject: ${message.subject}\n\n${message.body}`
    )).join('\n\n---\n\n')
    navigator.clipboard.writeText(text).then(() => setCompletedCount((c) => c + threadMessages.length))
  }

  const handleLogToCrm = () => {
    const entry = {
      id: `crm_${Date.now()}`,
      provider: crmProvider,
      channel,
      recipientType,
      title: editableSubject || `${RECIPIENT_LABELS[recipientType]} ${CHANNEL_LABELS[channel]}`,
      loggedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    }
    setCrmLog((current) => [entry, ...current].slice(0, 4))
  }

  const handleComplete = () => {
    const emailTemplates = savedTemplates.filter((t) => t.channel === 'email')
      .map((t) => ({ id: t.id, name: t.name, recipientType: t.recipientType }))
    updateDealState({
      outreachMaterials: {
        emailTemplates: emailTemplates.length
          ? emailTemplates
          : [{ id: 'm8_default', name: 'Generated Email', recipientType }],
        smsTemplates: savedTemplates.filter((t) => t.channel === 'sms').map((t) => t.name),
        callScripts: savedTemplates.filter((t) => t.channel === 'callscript').map((t) => t.name),
        generatedAt: new Date().toISOString(),
        channel,
        tone,
        lastRecipient: recipientType,
        followUpSequenceCreated: true,
        threadParties,
        crmLog,
        complianceScore,
        responseSuggestion,
        subjectVariants,
      },
    })
    updateAvailableModules()
    setTimeout(() => forceNavigateToModule(10), 500)
  }

  const checklistItems = [
    { label: 'Lender email drafted', done: generated && recipientType === 'lender' },
    { label: 'Broker outreach prepared', done: generated && recipientType === 'broker' },
    { label: 'Call script generated', done: generated && channel === 'callscript' },
    { label: 'SMS message ready', done: generated && channel === 'sms' },
    { label: 'Follow-up sequence reviewed', done: activeFollowUp !== null },
    { label: 'Compliance checked', done: generated && complianceScore >= 3 },
    { label: 'CRM activity logged', done: crmLog.length > 0 },
    { label: 'Template saved for reuse', done: savedTemplates.length > INITIAL_TEMPLATES.length },
  ]

  return (
    <div className="m8-page">
      {/* ── Header ── */}
      <div className="m8-header">
        <div className="m8-badge">Module 8</div>
        <div className="m8-header-text">
          <h1>Outreach &amp; Communication</h1>
          <p>AI-generated emails, SMS, and call scripts personalized to your deal data and recipient.</p>
        </div>
        <div className="m8-header-stats">
          <div className="m8-stat">
            <span>Templates Saved</span>
            <strong>{savedTemplates.length}</strong>
          </div>
          <div className="m8-stat">
            <span>Sent / Copied</span>
            <strong>{completedCount}</strong>
          </div>
        </div>
      </div>

      <div className="m8-grid">

        {/* ── Compose Panel ── */}
        <section className="m8-compose-card">
          <div className="m8-title-row">
            <span>Compose Message</span>
            <strong>AI Generator</strong>
          </div>

          {/* ── Editable Deal Context Strip ── */}
          <div className="m8-deal-ctx-bar">
            <div className="m8-deal-ctx-summary" onClick={() => setShowDealCtxEdit(v => !v)}>
              <span className="m8-deal-ctx-icon">🏢</span>
              <span className="m8-deal-ctx-name">{dealCtx.propName || 'No deal name'}</span>
              {dealCtx.city && <span className="m8-deal-ctx-pill">{[dealCtx.city, dealCtx.state].filter(Boolean).join(', ')}</span>}
              {dealCtx.units && Number(dealCtx.units) > 0 && <span className="m8-deal-ctx-pill">{dealCtx.units} units</span>}
              {dealCtx.price && Number(dealCtx.price) > 0 && <span className="m8-deal-ctx-pill">{money(Number(dealCtx.price))}</span>}
              <button className="m8-deal-ctx-edit-btn">{showDealCtxEdit ? '▲ Collapse' : '✏️ Edit Deal Info'}</button>
            </div>
            {showDealCtxEdit && (
              <div className="m8-deal-ctx-form">
                <div className="m8-ctx-row">
                  <label>
                    <span>Deal / Property Name</span>
                    <input type="text" value={dealCtx.propName} onChange={e => { updateCtx('propName', e.target.value); setGenerated(false); }} />
                  </label>
                  <label>
                    <span>City</span>
                    <input type="text" value={dealCtx.city} onChange={e => { updateCtx('city', e.target.value); setGenerated(false); }} />
                  </label>
                  <label>
                    <span>State</span>
                    <input type="text" value={dealCtx.state} onChange={e => { updateCtx('state', e.target.value); setGenerated(false); }} />
                  </label>
                </div>
                <div className="m8-ctx-row">
                  <label>
                    <span>Units</span>
                    <input type="number" value={dealCtx.units} onChange={e => { updateCtx('units', e.target.value); setGenerated(false); }} />
                  </label>
                  <label>
                    <span>Purchase Price ($)</span>
                    <input type="number" value={dealCtx.price} onChange={e => { updateCtx('price', e.target.value); setGenerated(false); }} />
                  </label>
                  <label>
                    <span>NOI ($)</span>
                    <input type="number" value={dealCtx.noi} onChange={e => { updateCtx('noi', e.target.value); setGenerated(false); }} />
                  </label>
                  <label>
                    <span>Cap Rate (%)</span>
                    <input type="number" step="0.1" value={dealCtx.capRate} onChange={e => { updateCtx('capRate', e.target.value); setGenerated(false); }} />
                  </label>
                </div>
                <p className="m8-ctx-note">Changes here update the deal data used in generated messages. The pipeline record is not affected.</p>
              </div>
            )}
          </div>

          <div className="m8-field-label">Channel</div>
          <div className="m8-seg">
            {Object.entries(CHANNEL_LABELS).map(([key, label]) => (
              <button key={key} className={channel === key ? 'active' : ''} onClick={() => { setChannel(key); setGenerated(false) }}>
                {channelIcon[key]} {label}
              </button>
            ))}
          </div>

          <div className="m8-field-label">Recipient Type</div>
          <div className="m8-seg">
            {Object.entries(RECIPIENT_LABELS).map(([key, label]) => (
              <button key={key} className={recipientType === key ? 'active' : ''} onClick={() => { setRecipientType(key); setGenerated(false) }}>
                {recipientIcon[key]} {label}
              </button>
            ))}
          </div>

          <div className="m8-field-label">Recipient Name (optional)</div>
          <input
            className="m8-input"
            type="text"
            placeholder={
              recipientType === 'lender' ? 'e.g. John Smith at Mesa Capital' :
                recipientType === 'broker' ? 'e.g. Sarah Lee, RE/MAX' :
                  'e.g. Owner Name'
            }
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
          />

          <div className="m8-field-label">Additional Notes (optional)</div>
          <textarea
            className="m8-textarea"
            rows={3}
            placeholder="e.g. Mention flexibility on seller concessions, or ask for a 2-week DD period…"
            value={extraNotes}
            onChange={(e) => setExtraNotes(e.target.value)}
          />

          <button className="m8-generate-btn" onClick={handleGenerate}>
            ✨ Generate {CHANNEL_LABELS[channel]}
          </button>
        </section>

        {/* ── Preview Panel ── */}
        <section className="m8-preview-card">
          <div className="m8-title-row">
            <span>Message Preview</span>
            <strong>{generated ? CHANNEL_LABELS[channel] : 'Awaiting Generation'}</strong>
          </div>

          {generated ? (
            <>
              {channel === 'email' && (
                <div className="m8-subject-row">
                  <span className="m8-subject-label">Subject:</span>
                  <input
                    className="m8-subject-input"
                    value={editableSubject}
                    onChange={(e) => setEditableSubject(e.target.value)}
                  />
                </div>
              )}
              <textarea
                className="m8-message-area"
                value={editableBody}
                onChange={(e) => setEditableBody(e.target.value)}
                rows={channel === 'callscript' ? 22 : 16}
              />
              <div className="m8-preview-actions">
                {channel === 'email' && (
                  <button className="m8-open-email-btn" onClick={handleOpenEmail}>
                    Open in Email
                  </button>
                )}
                {channel === 'callscript' && (
                  <div className="m8-ai-call-note">
                    📞 <strong>AI Call Assistant</strong> — Use this AI-generated script to guide your call.
                    AI-assisted live calling workflow coming soon.
                  </div>
                )}
                <button className="m8-copy-btn" onClick={() => { handleCopy(); setCompletedCount((c) => c + 1) }}>
                  {copied ? ' Copied!' : channel === 'email' ? '📋 Copy Email' : channel === 'sms' ? '📋 Copy SMS' : '📞 Copy Call Script'}
                </button>
                <button className="m8-save-tpl-btn" onClick={handleSaveTemplate}>
                  💾 Save as Template
                </button>
              </div>
            </>
          ) : (
            <div className="m8-empty-state">
              <div className="m8-empty-icon">{channelIcon[channel]}</div>
              <p>Select your channel &amp; recipient,<br />then click <strong>Generate</strong> to create<br />a personalized message.</p>
              <div className="m8-deal-preview">
                <div><span>Deal</span><strong>{dealCtx.propName || 'TBD'}</strong></div>
                <div><span>Location</span><strong>{location || 'TBD'}</strong></div>
                <div><span>Units</span><strong>{dealCtx.units && Number(dealCtx.units) > 0 ? dealCtx.units : '—'}</strong></div>
                <div><span>Price</span><strong>{Number(dealCtx.price) > 0 ? money(Number(dealCtx.price)) : '—'}</strong></div>
                {Number(dealCtx.noi) > 0 && <div><span>NOI</span><strong>{money(Number(dealCtx.noi))}</strong></div>}
                {Number(dealCtx.capRate) > 0 && <div><span>Cap Rate</span><strong>{Number(dealCtx.capRate).toFixed(1)}%</strong></div>}
              </div>
            </div>
          )}
        </section>

        {/* ── Templates Library ── */}
        <section className="m8-templates-card">
          <div className="m8-title-row">
            <span>Saved Templates</span>
            <strong>{savedTemplates.length} saved</strong>
          </div>
          <div className="m8-template-list">
            {savedTemplates.map((tpl) => (
              <div
                key={tpl.id}
                className="m8-tpl-row"
                role="button"
                tabIndex={0}
                onClick={() => handleOpenTemplate(tpl)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') handleOpenTemplate(tpl)
                }}
              >
                <div className="m8-tpl-icon">{channelIcon[tpl.channel]}</div>
                <div className="m8-tpl-info">
                  <strong>{tpl.name}</strong>
                  <span>{tpl.preview}</span>
                </div>
                <div className="m8-tpl-meta">
                  <span className="m8-pill">{RECIPIENT_LABELS[tpl.recipientType]}</span>
                  <button
                    className="m8-tpl-delete"
                    onClick={(event) => {
                      event.stopPropagation()
                      handleDeleteTemplate(tpl.id)
                    }}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
          {savedTemplates.length === 0 && (
            <p className="m8-no-templates">No templates yet. Generate a message and click "Save as Template".</p>
          )}
        </section>

        {/* ── Follow-up Sequence ── */}
        <section className="m8-followup-card">
          <div className="m8-title-row">
            <span>AI Follow-up Sequence</span>
            <strong>3-touch cadence</strong>
          </div>
          <p className="m8-followup-desc">
            Auto-generated follow-up messages for Day 3, Day 7, and Day 14 after initial outreach.
          </p>
          <div className="m8-followup-list">
            {followUps.map((fu) => (
              <div key={fu.day} className={`m8-fu-item ${activeFollowUp === fu.day ? 'open' : ''}`}>
                <button className="m8-fu-header" onClick={() => setActiveFollowUp(activeFollowUp === fu.day ? null : fu.day)}>
                  <span className="m8-fu-badge">Day {fu.day}</span>
                  <span className="m8-fu-label">{fu.label}</span>
                  <span className="m8-fu-arrow">{activeFollowUp === fu.day ? '▲' : '▼'}</span>
                </button>
                {activeFollowUp === fu.day && (
                  <div className="m8-fu-body">
                    <p>{fu.body}</p>
                    <button className="m8-fu-copy" onClick={() => { navigator.clipboard.writeText(fu.body); setCompletedCount((c) => c + 1) }}>
                      📋 Copy
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="m8-intel-card">
          <div className="m8-title-row">
            <span>Outreach Intelligence</span>
            <strong>For {recipientContext.target}</strong>
          </div>
          <div className="m8-recipient-clarity">
            <div>
              <span>Current outreach target</span>
              <strong>{recipientContext.target}</strong>
            </div>
            <p>{recipientContext.purpose}</p>
          </div>

          <div className="m8-tool-grid">
            <div className="m8-tool-box">
              <div className="m8-tool-head">
                <strong>{recipientContext.subjectLabel}</strong>
                <span>Client-ready</span>
              </div>
              <p className="m8-tool-note">
                These options are written for the selected recipient type and can be sent to the {recipientContext.target.toLowerCase()}.
              </p>
              <div className="m8-subject-options">
                {subjectVariants.map((variant) => (
                  <button key={variant.id} onClick={() => { setEditableSubject(variant.subject); setGenerated(true) }}>
                    <span>{variant.label}</span>
                    <strong>+{variant.lift}%</strong>
                    <small>{variant.subject}</small>
                  </button>
                ))}
              </div>
            </div>

            <div className="m8-tool-box">
              <div className="m8-tool-head">
                <strong>Compliance</strong>
                <span>{complianceScore}/{complianceChecks.length} passed</span>
              </div>
              <div className="m8-compliance-list">
                {complianceChecks.map((check) => (
                  <div key={check.label} className={check.pass ? 'pass' : 'warn'}>
                    <span>{check.pass ? '✓' : '!'}</span>
                    {check.label}
                  </div>
                ))}
              </div>
            </div>

            <div className="m8-tool-box">
              <div className="m8-tool-head">
                <strong>Response Suggestion</strong>
                <span>Reply-aware</span>
              </div>
              <textarea
                className="m8-mini-textarea"
                rows={4}
                placeholder={recipientContext.replyPlaceholder}
                value={incomingReply}
                onChange={(e) => setIncomingReply(e.target.value)}
              />
              <p className="m8-response-preview">{responseSuggestion}</p>
              <button className="m8-tool-action" onClick={() => { setEditableBody(responseSuggestion); setGenerated(true); setChannel('email') }}>
                Use Suggested Response
              </button>
            </div>

            <div className="m8-tool-box">
              <div className="m8-tool-head">
                <strong>Email &amp; CRM Log</strong>
                <span>{crmLog.length} logged</span>
              </div>
              <div className="m8-crm-row">
                {['Gmail', 'Outlook', 'CRM', 'M2M - CRM'].map((provider) => (
                  <button key={provider} className={crmProvider === provider ? 'active' : ''} onClick={() => setCrmProvider(provider)}>
                    {provider}
                  </button>
                ))}
              </div>
              {crmProvider === 'M2M - CRM' && (
                <div className="m8-ghl-banner">
                  <div className="m8-ghl-title">
                    <span>🔗</span>
                    <strong>M2M Integration — Using APIs Next Level</strong>
                  </div>
                  <p className="m8-ghl-label">M2M — CRM</p>
                  <p className="m8-ghl-note">
                    M2M URL / API key to be connected when provided by client.
                    Messages logged here will sync to your M2M pipeline once credentials are configured.
                  </p>
                </div>
              )}
              <button className="m8-tool-action" onClick={handleLogToCrm} disabled={!generated}>
                {crmProvider === 'M2M - CRM' ? 'Log to M2M' : 'Log Current Message'}
              </button>
              <div className="m8-crm-log">
                {crmLog.map((entry) => (
                  <div key={entry.id}>
                    <strong>{entry.provider}</strong>
                    <span>{entry.title} · {entry.loggedAt}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="m8-thread-manager">
            <div className="m8-tool-head">
              <strong>Multi-Party Thread Management</strong>
              <span>{threadMessages.length} coordinated drafts</span>
            </div>
            <div className="m8-thread-controls">
              {THREAD_PARTIES.map((party) => (
                <label key={party.key} className={threadParties.includes(party.key) ? 'active' : ''}>
                  <input type="checkbox" checked={threadParties.includes(party.key)} onChange={() => toggleThreadParty(party.key)} />
                  {party.label}
                </label>
              ))}
              <button onClick={handleCopyThread}>Copy Thread Pack</button>
            </div>
            <div className="m8-thread-list">
              {threadMessages.map(({ party, label, message }) => (
                <div key={party}>
                  <strong>{label}</strong>
                  <span>{message.subject}</span>
                  <p>{message.body.slice(0, 150)}...</p>
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>
      <div className="module-bottom-advance">
        <button className="m8-advance-btn" onClick={handleComplete}>
          Finalize Outreach &amp; Advance to Module 9
        </button>
      </div>
      {selectedTemplate && (
        <div className="m8-template-modal-overlay" onClick={() => setSelectedTemplate(null)}>
          <div
            className="m8-template-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="saved-template-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="m8-template-modal-header">
              <div>
                <span>{CHANNEL_LABELS[selectedTemplate.channel]}</span>
                <h2 id="saved-template-title">{selectedTemplate.name}</h2>
              </div>
              <button type="button" onClick={() => setSelectedTemplate(null)} aria-label="Close">×</button>
            </div>
            <div className="m8-template-modal-meta">
              <span>Recipient</span>
              <strong>{RECIPIENT_LABELS[selectedTemplate.recipientType]}</strong>
            </div>
            {selectedTemplate.subject && (
              <div className="m8-template-modal-section">
                <span>Subject</span>
                <strong>{selectedTemplate.subject}</strong>
              </div>
            )}
            <div className="m8-template-modal-section">
              <span>Message</span>
              <pre>{selectedTemplate.body}</pre>
            </div>
            <button className="m8-template-modal-close" type="button" onClick={() => setSelectedTemplate(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Module8Page
